export function StockTermsAndConditions() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <header className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Terms and Conditions of Service
          </h1>
          <h2 className="text-xl text-gray-600 mb-2">
            B2B and Store2B Ordering and Inventory Management Platform
          </h2>
          <p className="text-sm text-gray-500">
            Last Updated: 9/2/2025 | Version 2.1
          </p>
        </header>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="prose max-w-none text-gray-800 space-y-8">
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
              <p className="text-sm text-blue-800">
                <strong>Important Notice:</strong> By accessing or using our
                ordering and inventory management platform, you agree to be
                bound by these Terms and Conditions. Please read them carefully
                before proceeding with registration or placing any orders.
              </p>
            </div>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                1. Definitions and Interpretation
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>1.1</strong> In these Terms and Conditions, unless the
                  context otherwise requires:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>
                    <strong>"Platform"</strong> means our B2B and Store2B
                    ordering and inventory management system including all
                    software, websites, mobile applications, APIs, and related
                    services;
                  </li>
                  <li>
                    <strong>"Service Provider" or "We" or "Us"</strong> means
                    the entity operating and providing the Platform services;
                  </li>
                  <li>
                    <strong>"User" or "You"</strong> means any individual or
                    entity that accesses or uses the Platform;
                  </li>
                  <li>
                    <strong>"Supplier"</strong> means any business entity that
                    lists products or services on the Platform for sale to other
                    businesses or stores;
                  </li>
                  <li>
                    <strong>"Buyer"</strong> means any business entity or store
                    that purchases products or services through the Platform;
                  </li>
                  <li>
                    <strong>"B2B Transaction"</strong> means any commercial
                    transaction between business entities conducted through the
                    Platform;
                  </li>
                  <li>
                    <strong>"Store2B Transaction"</strong> means any commercial
                    transaction where a retail store purchases from a business
                    supplier through the Platform;
                  </li>
                  <li>
                    <strong>"Inventory"</strong> means all products, goods, or
                    items listed, tracked, or managed through the Platform;
                  </li>
                  <li>
                    <strong>"Order"</strong> means any request for products or
                    services submitted through the Platform;
                  </li>
                  <li>
                    <strong>"Agreement"</strong> means these Terms and
                    Conditions together with any additional terms, privacy
                    policy, and user agreements;
                  </li>
                  <li>
                    <strong>"Intellectual Property"</strong> means all
                    copyrights, trademarks, trade secrets, patents, and other
                    proprietary rights;
                  </li>
                  <li>
                    <strong>"Personal Data"</strong> means any information
                    relating to an identified or identifiable natural person;
                  </li>
                  <li>
                    <strong>"Force Majeure"</strong> means circumstances beyond
                    reasonable control including but not limited to acts of God,
                    war, terrorism, pandemic, natural disasters, government
                    actions, labor disputes, cyber attacks, or infrastructure
                    failures.
                  </li>
                </ul>
                <p>
                  <strong>1.2</strong> References to singular include plural and
                  vice versa. Headings are for convenience only and do not
                  affect interpretation.
                </p>
                <p>
                  <strong>1.3</strong> Any reference to legislation includes any
                  modification, extension, or re-enactment thereof.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                2. Platform Access and Account Registration
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>2.1 Registration Requirements:</strong> To access the
                  Platform, Users must create an account by providing accurate,
                  current, and complete information including but not limited to
                  business name, legal entity details, tax identification
                  numbers, contact information, and banking details.
                </p>
                <p>
                  <strong>2.2 Account Verification:</strong> We reserve the
                  right to verify all account information and may request
                  additional documentation including business licenses, tax
                  certificates, financial statements, or other credentials
                  before approving account activation.
                </p>
                <p>
                  <strong>2.3 Account Security:</strong> Users are solely
                  responsible for maintaining the confidentiality of their
                  account credentials and for all activities that occur under
                  their account. Users must immediately notify us of any
                  unauthorized use or security breach.
                </p>
                <p>
                  <strong>2.4 Account Suspension:</strong> We may suspend or
                  terminate accounts that violate these Terms, provide false
                  information, engage in fraudulent activities, or pose security
                  risks to the Platform or other Users.
                </p>
                <p>
                  <strong>2.5 Multi-User Access:</strong> Business accounts may
                  authorize multiple users with varying permission levels. The
                  account holder remains fully responsible for all actions taken
                  by authorized users.
                </p>
                <p>
                  <strong>2.6 Account Transfer:</strong> Accounts are
                  non-transferable without our prior written consent and may
                  require additional verification procedures.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                3. Order Management and Processing
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>3.1 Order Placement:</strong> Orders may be placed
                  through the Platform interface, API integration, or approved
                  third-party systems. All orders are subject to acceptance by
                  the respective Supplier and availability verification through
                  our inventory management system.
                </p>
                <p>
                  <strong>3.2 Order Confirmation:</strong> Orders are not
                  binding until confirmed by both the Supplier and reflected in
                  the Platform's order management system. Confirmation triggers
                  inventory allocation and payment processing procedures.
                </p>
                <p>
                  <strong>3.3 Order Modifications:</strong> Modifications to
                  confirmed orders may be possible subject to Supplier approval
                  and inventory availability. Additional charges may apply for
                  order changes, expedited processing, or custom requirements.
                </p>
                <p>
                  <strong>3.4 Order Cancellation:</strong> Cancellation policies
                  vary by Supplier and product type. Cancellations may incur
                  fees depending on order status, custom manufacturing
                  requirements, or restocking charges.
                </p>
                <p>
                  <strong>3.5 Bulk Orders:</strong> Large volume orders may
                  require special handling, extended processing times, or direct
                  coordination between parties. Volume discounts and special
                  terms may be available subject to negotiation.
                </p>
                <p>
                  <strong>3.6 Recurring Orders:</strong> The Platform supports
                  automated recurring order functionality. Users may establish
                  standing orders with specified frequencies, quantities, and
                  delivery schedules subject to inventory availability and
                  pricing updates.
                </p>
                <p>
                  <strong>3.7 Order Tracking:</strong> All orders are tracked
                  through our system providing real-time status updates,
                  shipping information, and delivery confirmations when
                  available.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                4. Inventory Management and Product Listings
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>4.1 Inventory Accuracy:</strong> Suppliers are
                  responsible for maintaining accurate inventory levels, product
                  descriptions, specifications, and pricing information.
                  Real-time inventory synchronization is recommended but not
                  guaranteed.
                </p>
                <p>
                  <strong>4.2 Product Information:</strong> All product listings
                  must include accurate descriptions, specifications, images,
                  pricing, availability status, and any relevant safety or
                  regulatory information. Misleading or false information is
                  strictly prohibited.
                </p>
                <p>
                  <strong>4.3 Inventory Allocation:</strong> The Platform
                  reserves inventory upon order confirmation based on
                  availability at time of purchase. Inventory allocation follows
                  first-come, first-served principles unless otherwise
                  specified.
                </p>
                <p>
                  <strong>4.4 Stock Management:</strong> Suppliers may set
                  minimum order quantities, maximum order limits, reorder
                  points, and safety stock levels. The Platform supports
                  automated reordering and low-stock notifications.
                </p>
                <p>
                  <strong>4.5 Product Categories:</strong> Products must be
                  properly categorized and tagged for effective search and
                  discovery. Miscategorization may result in listing removal or
                  account restrictions.
                </p>
                <p>
                  <strong>4.6 Restricted Products:</strong> Certain products may
                  be restricted or prohibited including but not limited to
                  hazardous materials, controlled substances, counterfeit goods,
                  or items requiring special licenses. Users must comply with
                  all applicable laws and regulations.
                </p>
                <p>
                  <strong>4.7 Quality Standards:</strong> All products must meet
                  applicable quality standards, safety requirements, and
                  regulatory compliance. Suppliers warrant that their products
                  are merchantable and fit for their intended purpose.
                </p>
                <p>
                  <strong>4.8 Inventory Audits:</strong> We reserve the right to
                  conduct inventory audits and request verification of stock
                  levels, product authenticity, and compliance with listing
                  requirements.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                5. Pricing and Payment Terms
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>5.1 Pricing Authority:</strong> Suppliers maintain
                  full control over their product pricing subject to any
                  agreed-upon pricing contracts or volume discount structures.
                  Prices may be updated in real-time or according to established
                  schedules.
                </p>
                <p>
                  <strong>5.2 Price Validity:</strong> Displayed prices are
                  valid at the time of viewing but may change before order
                  confirmation. Final pricing is confirmed upon order acceptance
                  and payment processing.
                </p>
                <p>
                  <strong>5.3 Payment Methods:</strong> The Platform supports
                  various payment methods including credit cards, bank
                  transfers, purchase orders, net payment terms, and digital
                  payment systems. Payment method availability may vary by
                  region and transaction type.
                </p>
                <p>
                  <strong>5.4 Payment Processing:</strong> Payments are
                  processed through secure, PCI-compliant systems. Payment
                  confirmation is required before order fulfillment unless
                  alternative credit terms have been established.
                </p>
                <p>
                  <strong>5.5 Credit Terms:</strong> Qualified businesses may
                  apply for credit terms including net 30, net 60, or custom
                  payment schedules. Credit approval is subject to financial
                  verification and ongoing performance monitoring.
                </p>
                <p>
                  <strong>5.6 Late Payments:</strong> Overdue payments may incur
                  late fees, interest charges, and account restrictions.
                  Persistent payment delays may result in account suspension or
                  termination.
                </p>
                <p>
                  <strong>5.7 Currency and Taxes:</strong> All transactions are
                  processed in the specified currency. Users are responsible for
                  applicable taxes, duties, and fees unless otherwise indicated.
                  Tax-exempt entities must provide valid exemption certificates.
                </p>
                <p>
                  <strong>5.8 Refunds and Credits:</strong> Refund policies vary
                  by Supplier and product type. Returns, exchanges, and refunds
                  are subject to individual Supplier policies and may require
                  approval through the Platform's return management system.
                </p>
                <p>
                  <strong>5.9 Payment Disputes:</strong> Payment disputes must
                  be reported promptly and will be investigated according to
                  established procedures. Chargebacks and payment reversals may
                  result in account restrictions pending resolution.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                6. Shipping and Delivery
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>6.1 Shipping Responsibility:</strong> Suppliers are
                  responsible for packaging, shipping, and delivering products
                  unless alternative arrangements are specified. Shipping costs,
                  methods, and timeframes are determined by individual
                  Suppliers.
                </p>
                <p>
                  <strong>6.2 Delivery Terms:</strong> Delivery terms including
                  FOB points, risk transfer, and title passage are specified in
                  individual transactions. Standard delivery terms apply unless
                  otherwise negotiated.
                </p>
                <p>
                  <strong>6.3 Shipping Insurance:</strong> Products may be
                  insured during transit at the discretion of the Supplier or as
                  requested by the Buyer. Insurance costs and claims procedures
                  vary by arrangement.
                </p>
                <p>
                  <strong>6.4 Delivery Timeframes:</strong> Estimated delivery
                  dates are provided for planning purposes but are not
                  guaranteed. Actual delivery times depend on product
                  availability, shipping method, destination, and external
                  factors.
                </p>
                <p>
                  <strong>6.5 Delivery Failures:</strong> Undeliverable
                  packages, refused deliveries, or incorrect shipping addresses
                  may result in additional charges. Buyers must provide accurate
                  delivery information and be available to receive shipments.
                </p>
                <p>
                  <strong>6.6 International Shipping:</strong> Cross-border
                  transactions are subject to customs regulations, import
                  duties, and additional documentation requirements. Buyers are
                  responsible for compliance with local import regulations.
                </p>
                <p>
                  <strong>6.7 Expedited Shipping:</strong> Rush orders and
                  expedited shipping may be available at additional cost subject
                  to product availability and Supplier capabilities.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                7. Quality Assurance and Returns
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>7.1 Product Quality:</strong> Suppliers warrant that
                  products conform to specifications, are free from defects, and
                  are suitable for their intended purpose. Quality standards
                  must meet or exceed industry norms and applicable regulations.
                </p>
                <p>
                  <strong>7.2 Inspection Rights:</strong> Buyers have the right
                  to inspect products upon receipt and must report any defects,
                  damages, or discrepancies within the specified timeframe,
                  typically within 5-10 business days.
                </p>
                <p>
                  <strong>7.3 Return Authorization:</strong> Returns require
                  prior authorization through the Platform's return management
                  system. Unauthorized returns may be refused or subject to
                  additional handling charges.
                </p>
                <p>
                  <strong>7.4 Return Conditions:</strong> Returned products must
                  be in original condition, properly packaged, and include all
                  original documentation. Custom or personalized items may not
                  be returnable.
                </p>
                <p>
                  <strong>7.5 Restocking Fees:</strong> Restocking fees may
                  apply to returns, particularly for special-order items, custom
                  products, or returns due to buyer error or change of mind.
                </p>
                <p>
                  <strong>7.6 Defective Products:</strong> Defective or
                  non-conforming products will be replaced, repaired, or
                  refunded at the Supplier's discretion. Warranty terms vary by
                  product and Supplier.
                </p>
                <p>
                  <strong>7.7 Return Shipping:</strong> Return shipping
                  arrangements and costs depend on the reason for return.
                  Defective products typically include prepaid return labels,
                  while other returns may be at the Buyer's expense.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                8. Data Privacy and Security
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>8.1 Data Protection:</strong> We implement
                  industry-standard security measures to protect User data
                  including encryption, secure transmission protocols, access
                  controls, and regular security audits.
                </p>
                <p>
                  <strong>8.2 Personal Information:</strong> Collection, use,
                  and protection of personal information is governed by our
                  Privacy Policy, which forms an integral part of these Terms
                  and Conditions.
                </p>
                <p>
                  <strong>8.3 Business Data:</strong> Business information
                  including transaction history, inventory data, and commercial
                  relationships is treated as confidential and is not shared
                  with unauthorized parties.
                </p>
                <p>
                  <strong>8.4 Data Sharing:</strong> Certain data may be shared
                  between transaction parties to facilitate orders, payments,
                  and deliveries. Users consent to necessary data sharing for
                  Platform functionality.
                </p>
                <p>
                  <strong>8.5 Data Retention:</strong> User data is retained
                  according to legal requirements, business needs, and User
                  preferences. Data deletion requests are honored subject to
                  legal and operational constraints.
                </p>
                <p>
                  <strong>8.6 Security Incidents:</strong> Security breaches or
                  data incidents will be investigated promptly and affected
                  Users notified according to applicable laws and regulations.
                </p>
                <p>
                  <strong>8.7 Third-Party Integrations:</strong> The Platform
                  may integrate with third-party services for enhanced
                  functionality. Third-party data handling is subject to their
                  respective privacy policies.
                </p>
                <p>
                  <strong>8.8 Compliance:</strong> Data handling practices
                  comply with applicable data protection laws including GDPR,
                  CCPA, and other regional privacy regulations.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                9. Intellectual Property Rights
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>9.1 Platform Ownership:</strong> The Platform,
                  including all software, interfaces, designs, and proprietary
                  technologies, is owned by us and protected by intellectual
                  property laws.
                </p>
                <p>
                  <strong>9.2 User Content:</strong> Users retain ownership of
                  their content including product descriptions, images, and
                  business information but grant us a limited license to use
                  such content for Platform operations.
                </p>
                <p>
                  <strong>9.3 Trademark Rights:</strong> Users may display their
                  trademarks and brand names on the Platform but must not
                  infringe upon others' intellectual property rights.
                </p>
                <p>
                  <strong>9.4 Copyright Compliance:</strong> All content must
                  comply with copyright laws. Users must not upload or
                  distribute copyrighted material without proper authorization.
                </p>
                <p>
                  <strong>9.5 Infringement Claims:</strong> Intellectual
                  property infringement claims will be investigated and
                  addressed according to established procedures including
                  content removal and account restrictions when appropriate.
                </p>
                <p>
                  <strong>9.6 License Grant:</strong> Users are granted a
                  limited, non-exclusive license to use the Platform for its
                  intended business purposes. This license may be revoked for
                  violations of these Terms.
                </p>
                <p>
                  <strong>9.7 Reverse Engineering:</strong> Users may not
                  reverse engineer, decompile, or attempt to derive source code
                  from the Platform or its components.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                10. Platform Usage and Conduct
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>10.1 Acceptable Use:</strong> Users must use the
                  Platform in accordance with its intended business purpose and
                  must not engage in activities that disrupt operations,
                  compromise security, or violate applicable laws.
                </p>
                <p>
                  <strong>10.2 Prohibited Activities:</strong> Prohibited
                  activities include but are not limited to: fraudulent
                  transactions, system abuse, spam, malware distribution,
                  unauthorized access attempts, data scraping, and circumvention
                  of security measures.
                </p>
                <p>
                  <strong>10.3 System Integrity:</strong> Users must not attempt
                  to overwhelm the Platform with excessive requests, automated
                  tools, or any activities that may degrade system performance
                  or availability.
                </p>
                <p>
                  <strong>10.4 API Usage:</strong> API access is subject to rate
                  limits, usage quotas, and additional terms. API abuse may
                  result in access restrictions or termination.
                </p>
                <p>
                  <strong>10.5 User Communications:</strong> Communications
                  between Users must be professional, accurate, and related to
                  legitimate business purposes. Harassment, spam, or
                  inappropriate content is prohibited.
                </p>
                <p>
                  <strong>10.6 Monitoring Rights:</strong> We reserve the right
                  to monitor Platform usage for security, compliance, and
                  quality assurance purposes while respecting User privacy
                  rights.
                </p>
                <p>
                  <strong>10.7 Compliance Obligations:</strong> Users must
                  comply with all applicable laws, regulations, and industry
                  standards relevant to their business activities and use of the
                  Platform.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                11. Warranties and Disclaimers
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>11.1 Platform Warranties:</strong> We warrant that the
                  Platform will perform substantially in accordance with its
                  documentation under normal use conditions. We do not warrant
                  uninterrupted or error-free operation.
                </p>
                <p>
                  <strong>11.2 Supplier Warranties:</strong> Product warranties
                  are provided by individual Suppliers and vary by product type,
                  manufacturer, and agreement terms. We are not responsible for
                  Supplier warranty performance.
                </p>
                <p>
                  <strong>11.3 Disclaimer of Warranties:</strong> Except as
                  expressly stated, the Platform is provided "as is" without
                  warranties of any kind, including but not limited to
                  merchantability, fitness for particular purpose, or
                  non-infringement.
                </p>
                <p>
                  <strong>11.4 Third-Party Services:</strong> Third-party
                  integrations and services are provided by external vendors. We
                  disclaim responsibility for third-party service performance,
                  availability, or support.
                </p>
                <p>
                  <strong>11.5 Data Accuracy:</strong> While we strive for
                  accuracy, we do not warrant the completeness, reliability, or
                  accuracy of information provided by Users or third parties.
                </p>
                <p>
                  <strong>11.6 System Availability:</strong> We aim for high
                  system availability but do not guarantee continuous,
                  uninterrupted access. Scheduled maintenance and unforeseen
                  outages may occur.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                12. Limitation of Liability
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>12.1 Liability Limitations:</strong> Our total
                  liability for any claims arising from or related to the
                  Platform or these Terms shall not exceed the fees paid by the
                  User in the twelve months preceding the claim.
                </p>
                <p>
                  <strong>12.2 Consequential Damages:</strong> We shall not be
                  liable for indirect, incidental, special, consequential, or
                  punitive damages including but not limited to lost profits,
                  business interruption, or data loss.
                </p>
                <p>
                  <strong>12.3 Force Majeure:</strong> We are not liable for
                  delays, failures, or damages caused by circumstances beyond
                  our reasonable control including natural disasters, government
                  actions, or infrastructure failures.
                </p>
                <p>
                  <strong>12.4 User Transactions:</strong> We are not a party to
                  transactions between Users and bear no responsibility for
                  transaction performance, product quality, payment disputes, or
                  delivery issues between parties.
                </p>
                <p>
                  <strong>12.5 Third-Party Actions:</strong> We are not
                  responsible for actions or omissions of third parties
                  including suppliers, carriers, payment processors, or other
                  service providers.
                </p>
                <p>
                  <strong>12.6 Data Loss:</strong> While we implement data
                  backup and recovery procedures, Users are responsible for
                  maintaining their own data backups and we disclaim liability
                  for data loss or corruption.
                </p>
                <p>
                  <strong>12.7 Jurisdictional Limitations:</strong> Some
                  jurisdictions do not allow limitation of certain damages, so
                  these limitations may not apply in all circumstances.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                13. Indemnification
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>13.1 User Indemnification:</strong> Users agree to
                  indemnify and hold us harmless from claims, damages, losses,
                  and expenses arising from their use of the Platform, violation
                  of these Terms, or infringement of third-party rights.
                </p>
                <p>
                  <strong>13.2 Product Liability:</strong> Suppliers indemnify
                  against claims related to their products including defects,
                  safety issues, regulatory non-compliance, and intellectual
                  property infringement.
                </p>
                <p>
                  <strong>13.3 Content Indemnification:</strong> Users indemnify
                  against claims arising from content they provide including
                  product descriptions, images, and other materials posted on
                  the Platform.
                </p>
                <p>
                  <strong>13.4 Defense Obligations:</strong> Indemnifying
                  parties must provide defense against covered claims and
                  cooperate in good faith with legal proceedings.
                </p>
                <p>
                  <strong>13.5 Settlement Rights:</strong> Indemnifying parties
                  may settle claims with prior written consent, which shall not
                  be unreasonably withheld.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                14. Term and Termination
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>14.1 Term:</strong> These Terms remain in effect while
                  Users access or use the Platform. Individual transactions may
                  have separate terms and completion requirements.
                </p>
                <p>
                  <strong>14.2 Termination Rights:</strong> Either party may
                  terminate their account with appropriate notice. We may
                  terminate accounts immediately for material breaches,
                  fraudulent activity, or legal violations.
                </p>
                <p>
                  <strong>14.3 Effect of Termination:</strong> Upon termination,
                  Platform access ceases but outstanding obligations including
                  payments and deliveries remain in effect until fulfilled.
                </p>
                <p>
                  <strong>14.4 Data Retention:</strong> Account data may be
                  retained for legal, operational, and archival purposes
                  according to our data retention policies and applicable laws.
                </p>
                <p>
                  <strong>14.5 Survival:</strong> Provisions regarding payment
                  obligations, intellectual property, liability limitations, and
                  dispute resolution survive termination.
                </p>
                <p>
                  <strong>14.6 Transition Assistance:</strong> We may provide
                  reasonable assistance for data export and transaction
                  completion during the termination transition period.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                15. Dispute Resolution
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>15.1 Negotiation:</strong> Disputes should first be
                  addressed through good faith negotiation between the parties
                  involved. We may facilitate communication and provide dispute
                  resolution tools.
                </p>
                <p>
                  <strong>15.2 Mediation:</strong> Unresolved disputes may be
                  submitted to mediation through a mutually agreed mediator or
                  mediation service before pursuing other remedies.
                </p>
                <p>
                  <strong>15.3 Arbitration:</strong> Disputes that cannot be
                  resolved through negotiation or mediation may be subject to
                  binding arbitration under established arbitration rules.
                </p>
                <p>
                  <strong>15.4 Platform Disputes:</strong> Disputes related to
                  Platform functionality, billing, or these Terms are subject to
                  the dispute resolution procedures specified herein.
                </p>
                <p>
                  <strong>15.5 Transaction Disputes:</strong> Disputes between
                  Users regarding specific transactions are primarily their
                  responsibility to resolve, though we may provide assistance
                  and dispute resolution tools.
                </p>
                <p>
                  <strong>15.6 Limitation Period:</strong> Claims must be
                  brought within one year of the event giving rise to the claim,
                  or they are time-barred.
                </p>
                <p>
                  <strong>15.7 Injunctive Relief:</strong> Nothing herein
                  prevents seeking injunctive relief for intellectual property
                  infringement or security breaches.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                16. Governing Law and Jurisdiction
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>16.1 Governing Law:</strong> These Terms are governed
                  by and construed in accordance with the laws of
                  [Jurisdiction], without regard to conflict of law principles.
                </p>
                <p>
                  <strong>16.2 Jurisdiction:</strong> Any legal proceedings
                  arising from these Terms or Platform use shall be subject to
                  the exclusive jurisdiction of courts in [Jurisdiction].
                </p>
                <p>
                  <strong>16.3 International Users:</strong> Users accessing the
                  Platform from other jurisdictions do so at their own risk and
                  are responsible for compliance with local laws.
                </p>
                <p>
                  <strong>16.4 Regulatory Compliance:</strong> Operations are
                  subject to applicable regulatory requirements including export
                  controls, trade sanctions, and industry-specific regulations.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                17. General Provisions
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>17.1 Entire Agreement:</strong> These Terms, together
                  with the Privacy Policy and any additional agreements,
                  constitute the entire agreement between parties regarding
                  Platform use.
                </p>
                <p>
                  <strong>17.2 Amendment:</strong> We reserve the right to
                  modify these Terms at any time. Material changes will be
                  communicated to Users with appropriate notice periods.
                </p>
                <p>
                  <strong>17.3 Severability:</strong> If any provision of these
                  Terms is deemed invalid or unenforceable, the remaining
                  provisions continue in full force and effect.
                </p>
                <p>
                  <strong>17.4 Waiver:</strong> Failure to enforce any provision
                  does not constitute a waiver of that provision or any other
                  rights under these Terms.
                </p>
                <p>
                  <strong>17.5 Assignment:</strong> Users may not assign their
                  rights or obligations without our prior written consent. We
                  may assign our rights and obligations with appropriate notice.
                </p>
                <p>
                  <strong>17.6 Force Majeure:</strong> Neither party is liable
                  for delays or failures caused by circumstances beyond their
                  reasonable control.
                </p>
                <p>
                  <strong>17.7 Notices:</strong> All notices must be in writing
                  and delivered to the addresses specified in User accounts or
                  these Terms.
                </p>
                <p>
                  <strong>17.8 Language:</strong> These Terms are prepared in
                  English. Any translations are provided for convenience only,
                  and the English version prevails in case of conflicts.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                18. Contact Information
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>18.1 Support:</strong> For technical support, account
                  assistance, or general inquiries, contact our support team
                  through the designated channels provided in your account
                  dashboard.
                </p>
                <p>
                  <strong>18.2 Legal Notices:</strong> Legal notices and formal
                  communications should be directed to our designated legal
                  contact information available upon request.
                </p>
                <p>
                  <strong>18.3 Business Hours:</strong> Support and service
                  availability may vary by region and service level agreements.
                  Emergency support procedures are available for critical
                  issues.
                </p>
                <p>
                  <strong>18.4 Feedback:</strong> We welcome User feedback and
                  suggestions for Platform improvements. Submitted feedback may
                  be used for development purposes without compensation.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                19. Compliance and Regulatory Matters
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>19.1 Regulatory Compliance:</strong> Users must comply
                  with all applicable laws and regulations in their jurisdiction
                  including but not limited to business licensing, tax
                  obligations, product safety standards, and trade regulations.
                </p>
                <p>
                  <strong>19.2 Export Controls:</strong> International
                  transactions are subject to export control laws and trade
                  sanctions. Users are responsible for obtaining necessary
                  licenses and ensuring compliance.
                </p>
                <p>
                  <strong>19.3 Industry Standards:</strong> Users in regulated
                  industries must maintain compliance with applicable industry
                  standards and certification requirements.
                </p>
                <p>
                  <strong>19.4 Audit Rights:</strong> We reserve the right to
                  audit User compliance with these Terms and applicable
                  regulations as necessary for Platform integrity and legal
                  compliance.
                </p>
                <p>
                  <strong>19.5 Reporting Obligations:</strong> Users must
                  provide necessary documentation and reports for tax,
                  regulatory, and legal compliance purposes.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b pb-2">
                20. Acknowledgment and Acceptance
              </h2>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>20.1 Agreement to Terms:</strong> By accessing or
                  using the Platform, Users acknowledge that they have read,
                  understood, and agree to be bound by these Terms and
                  Conditions.
                </p>
                <p>
                  <strong>20.2 Authority:</strong> Users represent that they
                  have the necessary authority to enter into this Agreement on
                  behalf of their organization and that the individual accepting
                  these Terms is authorized to do so.
                </p>
                <p>
                  <strong>20.3 Regular Review:</strong> Users are encouraged to
                  review these Terms regularly as they may be updated to reflect
                  changes in law, regulation, or Platform functionality.
                </p>
                <p>
                  <strong>20.4 Continued Use:</strong> Continued use of the
                  Platform after Terms modifications constitutes acceptance of
                  the updated Terms.
                </p>
              </div>
            </section>

            <div className="bg-gray-50 border rounded-lg p-6 mt-12">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Effective Date and Version Information
              </h3>
              <p className="text-sm text-gray-600 mb-2">
                These Terms and Conditions are effective as of{" "}
                {new Date().toLocaleDateString()} and supersede all previous
                versions.
              </p>
              <p className="text-sm text-gray-600 mb-2">
                Version 2.1 - Updated for enhanced B2B and Store2B functionality
                and compliance requirements.
              </p>
              <p className="text-sm text-gray-600">
                For questions about these Terms, please contact our legal
                department through the official channels provided in your
                account.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
